import {
  NotificationListService,
  NotificationType,
} from '@affine/core/modules/notification';
import { useLiveData, useService } from '@toeverything/infra';
import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { useEffect, useCallback, useRef } from 'react';

const updateAvailableAtom = atom(false);
const updateInfoAtom = atom<{
  latestVersion: string | null;
  timestamp: string | null;
  isForceUpdate?: boolean;
  updateContent?: Record<string, string | undefined> | null;
} | null>(null);

let updateCheckInterval: NodeJS.Timeout | null = null;
const currentVersion = BUILD_CONFIG.appVersion;
function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(n => parseInt(n, 10));
  const pb = b.split('.').map(n => parseInt(n, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
}

export const useWebUpdater = () => {
  const [updateAvailable, setUpdateAvailable] = useAtom(updateAvailableAtom);
  const [updateInfo, setUpdateInfo] = useAtom(updateInfoAtom);
  const checkingRef = useRef(false);
  const notificationListService = useService(NotificationListService);
  const notifications = useLiveData(notificationListService.notifications$);
  // 持有最新的 notifications，避免将 notifications 放进 useEffect 依赖导致重复注册与泄漏
  const notificationsRef = useRef<typeof notifications>(notifications);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);
  const getNotificationList = () => {
    notificationListService.reset();
    notificationListService.loadMore();
  };
  const waitForNotificationLoaded = useCallback(
    async (timeoutMs = 8000) => {
      const startAt = Date.now();
      // 触发了 loadMore 之后，等待一次 isLoading=true -> false 的落地；若未经历 loading，则按超时或数据已到达判断
      let seenLoading = false;
      // 快速路径：如果已有数据，直接返回
      if (notificationListService.notifications$.value.length > 0) return;
      // 轮询等待，避免耦合 LiveData 具体订阅 API
      while (Date.now() - startAt < timeoutMs) {
        // 记录是否进入过 loading
        if (notificationListService.isLoading$.value) {
          seenLoading = true;
        }
        // 条件：加载完成且（进入过loading或已有数据）
        const loaded =
          (!notificationListService.isLoading$.value && seenLoading) ||
          notificationListService.notifications$.value.length > 0;
        if (loaded) return;
        await new Promise(r => setTimeout(r, 50));
      }
    },
    [notificationListService]
  );
  const checkUpdate = useCallback(async () => {
    if (checkingRef.current) return;

    checkingRef.current = true;
    const latestNotifications = notificationsRef.current;
    try {
      const info = latestNotifications?.[0];
      // 类型判断（两种方式：优先 enum，没有就字符串兜底；再不行就只看 body 字段）
      const isWebVersionUpdate =
        info?.type === (NotificationType as any)?.WebVersionUpdate ||
        (info?.body && 'version' in info.body);
      if (!isWebVersionUpdate) return;
      const latest = (info?.body?.version as string | undefined) ?? '';
      const isForce = !!info?.body?.isForceUpdate;
      // 允许后端返回更新内容
      const updateContent =
        (info?.body?.updateContent as Record<string, string | undefined>) ??
        null;
      const result = compareSemver(latest, currentVersion);

      if (latest && result > 0) {
        setUpdateInfo({
          latestVersion: latest,
          timestamp: null, // 如后端有时间戳也可带上
          isForceUpdate: isForce,
          updateContent,
        });
        setUpdateAvailable(true);
      }
    } finally {
      checkingRef.current = false;
    }
  }, [setUpdateAvailable, setUpdateInfo, notificationListService]);
  // 初始化时检查
  useEffect(() => {
    // 定期检查更新（每 5 分钟）
    updateCheckInterval = setInterval(
      () => {
        (async () => {
          getNotificationList();
          await waitForNotificationLoaded();
          await checkUpdate();
        })();
      },
      5 * 60 * 1000
    );

    return () => {
      if (updateCheckInterval) {
        clearInterval(updateCheckInterval);
      }
    };
  }, []);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    setUpdateAvailable(false);
  }, [setUpdateAvailable]);

  return {
    updateAvailable,
    updateInfo,
    currentVersion,
    handleRefresh,
    handleDismiss,
    checkUpdate,
  };
};

// 监听 visibilitychange 事件，当用户切换回标签页时检查更新
export const useWebUpdaterOnVisible = () => {
  const { checkUpdate } = useWebUpdater();

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkUpdate]);
};
